import React, { ReactElement, Fragment, useContext } from 'react'
import { useIntl } from 'react-intl'
import { PureQueryOptions } from 'apollo-client'
import { useDateRange, DateFilter } from './use-date-range'
import {
  Grid,
  Column,
  MaxCellHeightsByRow
} from '../../legacy-design-system/grid'
import { GridLoader, GridStateManager } from '../grid-data'
import { RowSelection } from '../../legacy-design-system/grid/grid'
import {
  AggregationConfigs,
  AggregationOptions,
  AggregationConfig,
  Aggregations
} from '../aggregations'
import {
  OrderFieldConfig,
  Order,
  OrderDirection
} from '../../legacy-design-system/order'
import { DocumentNode } from 'graphql'
import {
  Vulnerability,
  VulnerabilityDateField,
  VulnerabilityOrSinkGroup
} from './shared'
import { DateRangeOption } from '../../legacy-design-system/date-picker'
import { DateRange } from '../../legacy-design-system/date-picker/shared'
import { Role } from '@contrast-security-inc/base-types'
import { AgentFeaturesLoader, AgentFeature } from '../agent-features'
import { JobTracker } from '../job-tracker'
import { DeleteOverlay } from './delete-overlay'
import {
  OnGroupToggleProps,
  OnOptionToggleProps,
  OnMultiGroupToggleProps
} from '../../legacy-design-system/filter'
import { OrganizationType } from '../viewer/shared'
import { QuickFilter } from '../../legacy-design-system/quick-filter'
import { RouteContext, RouteContextValue } from '../route-provider'
import { LegacyGridContainer } from '../../legacy-design-system/legacy-main'
import { SlidingTutorial } from '../sliding-tutorial/sliding-tutorial'
import { SessionMetadataField } from '../session-metadata-field'
import {
  MetadataFilter,
  MetadataFilterManager
} from '../filter-manager/metadata-filter-manager'
import { SessionMetadataAggregation } from '../session-metadata-aggregation'
import { FilterType } from '../filter-manager'
import { ChartVisualizationManager } from './chart-visualization-manager'
import { ChartToggleProps } from './chart-toggle'
import { ChartLoader, getChartSnapshotInterval } from './chart-loader'
import { VulnerabilitySeveritySnapshotBreakdown } from './vulnerabilities-severity-chart-loader'
import { VulnerabilityDiscoverySnapshotBreakdown } from './vulnerabilities-discovery-chart-loader'
import { getSessionMetadataSelectionsValues } from './chart-container'
import { CollectionVariables } from '../organization-collection-loader'
import { sendDefaultEvent } from '../analytics'
import { HeaderCheckboxes } from '../../legacy-design-system/grid/header/header-checkboxes'
import { getVulnerabilityOrSinkGroup } from './row-query'
import { GQLVulnerability } from '../../graphql/schema/types/vulnerability'
import { GQLVulnerabilityOrSinkGroup } from '../../graphql/schema/types/vulnerability-sink-group'
import { BackToGroups } from './back-to-groups-button'
import { serverEnvI18n } from '../server-environment'
import { useCurrentOrganization, useCurrentUser } from 'contrastBase/state'

const batchSize = 25
const minRowHeight = 70

function quickFilterToGroup() {
  return 'other'
}

function sessionMetadataFieldToGroup({ id }) {
  switch (id) {
    case 'dissociated':
      return 'dissociated'
    default:
      return 'other'
  }
}

interface ColumnArgs<TAggregationCategory extends string> {
  organizationID: string
  organizationRole: Role
  hasLicenses: boolean
  dateRange?: DateRange
  dateRangeOption?: DateRangeOption
  features: AgentFeature[]
  refresh: () => Promise<any>
  refetchQueries: PureQueryOptions[]
  onSetCustomDateRange: (dateRange: DateRange) => void
  onSetDateRangeOption: (dateRangeOption: DateRangeOption) => void
  onClearDateRange: () => void
  dateField?: VulnerabilityDateField
  onSetDateField: (dateField: VulnerabilityDateField) => void
  trackJob: (job: {
    id: string
    total: number
    progress: number
  }) => Promise<void>

  aggregations: Aggregations<TAggregationCategory>
  sessionMetadataFieldID?: string
  sessionMetadataAggregations?: SessionMetadataAggregation[]
  metadataSelections?: MetadataFilter[]
  onClearMetadataSelections?: () => void
  onMetadataOptionsToggle?: (groupID: string, optionIDs: string[]) => void
  restrictedEditRole?: boolean
  protectAssessEnv?: string
  isProtectColumnEnabled?: boolean
  isProtectEnabled?: boolean
}

interface LicenseBreakdown {
  used: number
  unused: number
}

interface Index {
  index: number
}

export interface VulnerabilitiesGridContextValueData<
  TAggregationCategory extends string,
  TAdditionalCollectionProps
> {
  features: AgentFeature[]
  totalRowCount: number
  loadMore: () => Promise<any>
  refresh: () => Promise<any>
  organizationRole: Role
  organizationID: string
  organizationType: OrganizationType
  assessLicenseBreakdown: LicenseBreakdown
  additionalCollectionProps: TAdditionalCollectionProps
  additionalFilters?: { [key: string]: any }
  quickFilters: QuickFilter[]
  aggregations: Aggregations<TAggregationCategory>
  sessionMetadataFields?: SessionMetadataField[]
  sessionMetadataAggregations?: SessionMetadataAggregation[]
  severityChartSnapshots?: VulnerabilitySeveritySnapshotBreakdown[]
  discoveryChartSnapshots?: VulnerabilityDiscoverySnapshotBreakdown[]
  loadingSnapshots?: boolean
  refetchQueries: PureQueryOptions[]
  loadedRowCount: number
  rowGetter: (params: Index) => any
  rowIndexFinder: (
    predicate: (
      value: VulnerabilityOrSinkGroup,
      index: number,
      obj: VulnerabilityOrSinkGroup[]
    ) => boolean
  ) => number
  isRowLoaded: (params: Index) => boolean
  rowDataValue: unknown
  protectAssessEnv?: string
  licensedOnly?: boolean
  isSinkDrilldown?: boolean
  isGroupBySinkEnabled?: boolean
  restrictedEditRole?: boolean
}

export interface VulnerabilitiesGridContextValue<
  TOrderField extends string,
  TAggregationCategory extends string,
  TAdditionalCollectionProps = {},
  TChartVisualization = {}
> {
  order: Order<TOrderField>
  filterText: string
  quickFilterID: string

  protectAssessEnv: string
  isProtectColumnEnabled?: boolean
  isProtectEnabled?: boolean

  sessionMetadataFieldID?: string
  onSetSortField: (field: TOrderField) => void
  onSetSortDirection: (direction: OrderDirection) => void
  orderFieldConfig: OrderFieldConfig<TOrderField>
  defaultOrderField: TOrderField
  onFilterTextChange: (value: string) => void
  onQuickFilterIDChange: (quickFilterID: string) => void
  onSessionMetadataFieldIDChange?: (sessionMetadataFieldID: string) => void
  onMultiGroupToggle: (
    props: OnMultiGroupToggleProps<TAggregationCategory>
  ) => void
  onGroupToggle: (props: OnGroupToggleProps<TAggregationCategory>) => void
  onOptionToggle: (props: OnOptionToggleProps<TAggregationCategory>) => void
  aggregationConfigs: Record<TAggregationCategory, AggregationConfig>
  aggregationSelections: AggregationOptions<TAggregationCategory>
  onAggregationCategoriesClear: (categories: TAggregationCategory[]) => void
  metadataSelections?: MetadataFilter[]
  onClearMetadataSelections?: () => void
  onMetadataOptionsToggle?: (groupID: string, optionIDs: string[]) => void

  isChartContainerCollapsed?: boolean
  chartVisualization?: TChartVisualization
  onChartContainerToggle: () => void
  onChartVisualizationToggle: (chartVisualization: TChartVisualization) => void

  updateCellHeight: (rowDataID: string, height: number) => void
  registerListRef: (ref: any) => void
  maxCellHeightsByRow: MaxCellHeightsByRow
  dateRangeOption?: DateRangeOption
  dateRange?: DateRange
  onSetCustomDateRange: (dateRange: DateRange) => void
  onSetDateRangeOption: (dateRangeOption: DateRangeOption) => void
  onClearDateRange: () => void
  dateField?: VulnerabilityDateField
  onSetDateField: (dateField: VulnerabilityDateField) => void
  toggleQueryParameter: RouteContextValue['toggleQueryParameter']
  toggleQueryParameters: RouteContextValue['toggleQueryParameters']
  dateFilter?: DateFilter<VulnerabilityDateField>
  resetFilters: () => void
  rootScope?: {
    $on: (eventName: string, callback: any) => void
  }

  data?: VulnerabilitiesGridContextValueData<
    TAggregationCategory,
    TAdditionalCollectionProps
  >
}
interface LicensedCount {
  licensedVulnerabilityCount: number
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {}

export function createVulnerabilitiesGridContext<
  TOrderField extends string,
  TAggregationCategory extends string,
  TChartVisualizationType = {}
>({
  initialOrder,
  aggregationConfigs,
  initialAggregationSelections,
  orderFieldConfig
}: {
  aggregationConfigs: AggregationConfigs<TAggregationCategory>
  initialAggregationSelections: AggregationOptions<TAggregationCategory>
  orderFieldConfig: OrderFieldConfig<TOrderField>
  initialOrder: TOrderField
}): React.Context<
  VulnerabilitiesGridContextValue<
    TOrderField,
    TAggregationCategory,
    LicensedCount,
    TChartVisualizationType
  >
> {
  return React.createContext<
    VulnerabilitiesGridContextValue<
      TOrderField,
      TAggregationCategory,
      LicensedCount,
      TChartVisualizationType
    >
  >({
    order: {
      field: initialOrder,
      direction: orderFieldConfig[initialOrder].direction
    },
    orderFieldConfig,
    defaultOrderField: initialOrder,
    onSetSortField: noop,
    onSetSortDirection: noop,
    protectAssessEnv: 'PRODUCTION',
    filterText: '',
    onFilterTextChange: noop,
    quickFilterID: 'OPEN',
    onQuickFilterIDChange: noop,
    onSessionMetadataFieldIDChange: noop,
    onMetadataOptionsToggle: noop,
    onClearMetadataSelections: noop,
    metadataSelections: [],
    aggregationConfigs,
    aggregationSelections: initialAggregationSelections,
    onMultiGroupToggle: noop,
    onGroupToggle: noop,
    onOptionToggle: noop,
    onAggregationCategoriesClear: noop,
    onChartContainerToggle: noop,
    onChartVisualizationToggle: noop,
    updateCellHeight: noop,
    registerListRef: noop,
    maxCellHeightsByRow: {},
    onSetCustomDateRange: noop,
    onSetDateRangeOption: noop,
    onClearDateRange: noop,
    dateField: 'FIRST',
    onSetDateField: noop,
    resetFilters: noop,
    toggleQueryParameter: noop,
    toggleQueryParameters: noop,
    rootScope: { $on: noop }
  })
}

interface ComparatorVariables extends CollectionVariables {
  sessionMetadata?: MetadataFilter[]
}

export const vulnsGridAreVariablesEqual = (
  var1: ComparatorVariables,
  var2: ComparatorVariables
) => {
  if (var1.sessionMetadata && var2.sessionMetadata) {
    const var1SessionMetadata = var1.sessionMetadata
    const var2SessionMetadata = var2.sessionMetadata

    return (
      var1SessionMetadata.length === var2SessionMetadata.length &&
      var1SessionMetadata.every(
        ({ fieldID: var1FieldID, values: var1Values }) => {
          const var2Metadata = var2SessionMetadata.find(
            ({ fieldID: var2FieldID }) => var1FieldID === var2FieldID
          )

          return (
            !!var2Metadata &&
            var1Values.length === var2Metadata.values.length &&
            new Set([...var1Values, ...var2Metadata.values]).size ===
              var1Values.length
          )
        }
      )
    )
  }
  return true
}

// Filters sort dropdown when we're grouping vulnerabilities by sink
export function filterOrderFieldConfig<TOrderField extends string>(
  orderFieldConfig: OrderFieldConfig<TOrderField>,
  isGroupBySinkEnabled: boolean
): OrderFieldConfig<TOrderField> {
  if (isGroupBySinkEnabled) {
    return Object.keys(orderFieldConfig)
      .filter((key) => !['NAME', 'APPLICATION', 'STATUS'].includes(key))
      .reduce(
        (previousObject, currentKey) => ({
          ...previousObject,
          [currentKey]: orderFieldConfig[currentKey]
        }),
        {}
      ) as OrderFieldConfig<TOrderField>
  }
  return orderFieldConfig
}

export function createVulnerabilitiesGridContextProvider<
  TOrderField extends string,
  TAggregationCategory extends string,
  TChartVisualization extends {} = {}
>(
  VulnerabilitiesGridContext: React.Context<
    VulnerabilitiesGridContextValue<TOrderField, TAggregationCategory>
  >,
  aggregationConfigs: AggregationConfigs<TAggregationCategory>,
  filterType: FilterType,
  defaultOrderField: TOrderField,
  orderFieldConfig: OrderFieldConfig<TOrderField>,
  getQueries: (routeParameters: string[]) =>
    | {
        pageQuery: DocumentNode
        aggregationsQueries: DocumentNode[]
        quickFiltersQuery: DocumentNode
        sessionMetadataFieldsQuery?: DocumentNode
        sessionMetadataAggregationQuery?: DocumentNode
        severitySnapshotsQuery?: DocumentNode
        discoverySnapshotsQuery?: DocumentNode
      }
    | undefined,
  shouldLoadData: (routeParameters: string[]) => boolean,
  chartVisualization?: TChartVisualization
) {
  return function VulnerabilitiesGridContextProvider({
    rootScope,
    children
  }: {
    rootScope?: {
      $on: (eventName: string, callback: any) => void
    }
    children?: React.ReactNode
  }) {
    const {
      dateFilter,
      dateRangeOption,
      dateRange,
      onSetCustomDateRange,
      onSetDateRangeOption,
      onClearDateRange,
      dateField,
      onSetDateField
    } = useDateRange<VulnerabilityDateField>('LAST')

    const {
      toggleQueryParameter,
      toggleQueryParameters,
      isCurrentQueryParam,
      parameters: routeParameters
    } = useContext(RouteContext)

    const isGroupBySinkEnabled = isCurrentQueryParam('groupedBy', 'SINK')
    const licensedOnly = isCurrentQueryParam('licensedOnly', 'true')
    const isSinkDrilldown = isCurrentQueryParam('drilldown', 'true')
    const filteredOrderFieldConfig = filterOrderFieldConfig(
      orderFieldConfig,
      isGroupBySinkEnabled
    )

    const intl = useIntl()
    const protectAssessEnv = intl.formatMessage({
      id: serverEnvI18n[
        useCurrentOrganization()?.currentOrganization?.properties
          ?.protect_environment_displayed_in_vulnerability_grid || 'PRODUCTION'
      ]
    })
    const { currentUser: { isProtectEnabled } = {} } = useCurrentUser()

    return (
      <GridStateManager
        defaultOrderField={defaultOrderField}
        orderFieldConfig={filteredOrderFieldConfig}
        initialQuickFilterID="OPEN"
        filterType={filterType}
      >
        {({
          order,
          onSetSortField,
          onSetSortDirection,
          filterText,
          onFilterTextChange,
          quickFilterID,
          onQuickFilterIDChange,
          sessionMetadataFieldID,
          onSessionMetadataFieldIDChange,
          aggregationSelections,
          onGroupToggle,
          onOptionToggle,
          onMultiGroupToggle,
          onAggregationCategoriesClear,
          updateCellHeight,
          registerListRef,
          maxCellHeightsByRow,
          resetFilters
        }) => {
          return (
            <MetadataFilterManager>
              {({
                metadataSelections,
                onClearMetadataSelections,
                onMetadataOptionsToggle
              }) => {
                return (
                  <ChartVisualizationManager<TChartVisualization>
                    chartVisualization={chartVisualization}
                  >
                    {({
                      isChartContainerCollapsed,
                      chartVisualization,
                      onChartContainerToggle,
                      onChartVisualizationToggle
                    }) => {
                      const providerValue = {
                        order,
                        orderFieldConfig: filteredOrderFieldConfig,
                        defaultOrderField,
                        onSetSortField,
                        onSetSortDirection,

                        protectAssessEnv,
                        isProtectEnabled,

                        filterText,
                        onFilterTextChange,
                        quickFilterID,
                        onQuickFilterIDChange,
                        sessionMetadataFieldID,
                        onSessionMetadataFieldIDChange,

                        metadataSelections,
                        onClearMetadataSelections,
                        onMetadataOptionsToggle,

                        aggregationSelections,
                        aggregationConfigs,
                        onMultiGroupToggle,
                        onGroupToggle,
                        onOptionToggle,
                        onAggregationCategoriesClear,

                        isChartContainerCollapsed,
                        chartVisualization,
                        onChartContainerToggle,
                        onChartVisualizationToggle,

                        updateCellHeight,
                        registerListRef,
                        maxCellHeightsByRow,
                        dateFilter,
                        dateRangeOption,
                        dateRange,
                        onSetCustomDateRange,
                        onSetDateRangeOption,
                        onClearDateRange,
                        dateField,
                        onSetDateField,
                        resetFilters: () => {
                          resetFilters()
                          onClearDateRange()
                          onClearMetadataSelections()
                        },
                        toggleQueryParameter,
                        toggleQueryParameters,
                        rootScope
                      }
                      return (
                        <AgentFeaturesLoader
                          render={({ features = [] }) => {
                            const queries = getQueries(routeParameters)
                            const loadData = shouldLoadData(routeParameters)

                            if (!queries || !loadData) {
                              return (
                                <VulnerabilitiesGridContext.Provider
                                  value={providerValue}
                                >
                                  {children}
                                </VulnerabilitiesGridContext.Provider>
                              )
                            }

                            let additionalFilters: {
                              [key: string]: any
                            } = {}

                            if (dateFilter) {
                              additionalFilters = {
                                ...additionalFilters,
                                dateFilter
                              }
                            }

                            if (metadataSelections) {
                              if (filterType === 'applications-vulns') {
                                additionalFilters = {
                                  ...additionalFilters,
                                  sessionMetadata: metadataSelections
                                }
                              } else if (filterType === 'vulns') {
                                additionalFilters = {
                                  ...additionalFilters,
                                  applicationMetadata: metadataSelections
                                }
                              }
                            }

                            return (
                              <GridLoader<
                                Vulnerability,
                                TOrderField,
                                TAggregationCategory,
                                LicensedCount
                              >
                                aggregationsQueries={
                                  queries.aggregationsQueries
                                }
                                aggregationsQueryRootKey="vulnerabilityAggregations"
                                batchSize={batchSize}
                                quickFiltersQuery={queries.quickFiltersQuery}
                                quickFiltersQueryRootKey="vulnerabilityQuickFilters"
                                sessionMetadataFieldsQuery={
                                  queries.sessionMetadataFieldsQuery
                                }
                                sessionMetadataFieldsQueryRootKey="sessionMetadataAppFields"
                                sessionMetadataAggregationQuery={
                                  queries.sessionMetadataAggregationQuery
                                }
                                collectionQuery={queries.pageQuery}
                                collectionQueryRootKey={
                                  'vulnerabilitiesAndSinkGroups'
                                }
                                filterText={filterText}
                                order={order}
                                quickFilterID={quickFilterID}
                                sessionMetadataFieldID={sessionMetadataFieldID}
                                aggregationBucketIDConfigs={aggregationConfigs}
                                aggregationFilters={aggregationSelections}
                                onSessionMetadataFieldIDChange={
                                  onSessionMetadataFieldIDChange
                                }
                                additionalFilters={additionalFilters}
                                areVariablesEqual={vulnsGridAreVariablesEqual}
                                licensedOnly={licensedOnly}
                                isGroupBySinkEnabled={isGroupBySinkEnabled}
                              >
                                {({
                                  totalRowCount,
                                  loadMore,
                                  organizationID,
                                  organizationRole,
                                  organizationType,
                                  assessLicenseBreakdown,
                                  quickFilters,
                                  aggregations,
                                  sessionMetadataFields,
                                  sessionMetadataAggregations,
                                  loadedRowCount,
                                  rowDataValue,
                                  rowGetter,
                                  rowIndexFinder,
                                  isRowLoaded,
                                  refetchQueries,
                                  refresh,
                                  additionalCollectionProps,
                                  restrictedEditRole
                                }) => {
                                  const VulnerabilitiesGridContextProvider = (
                                    extraDataProps?: any
                                  ) => {
                                    return (
                                      <VulnerabilitiesGridContext.Provider
                                        value={{
                                          ...providerValue,
                                          rootScope,
                                          data: {
                                            features,
                                            totalRowCount,
                                            loadMore,
                                            organizationID,
                                            organizationRole,
                                            organizationType,
                                            assessLicenseBreakdown,
                                            quickFilters,
                                            aggregations,
                                            sessionMetadataFields,
                                            sessionMetadataAggregations,
                                            loadedRowCount,
                                            rowDataValue,
                                            rowGetter,
                                            rowIndexFinder,
                                            isRowLoaded,
                                            refetchQueries,
                                            refresh,
                                            additionalCollectionProps,
                                            additionalFilters,
                                            licensedOnly,
                                            isSinkDrilldown,
                                            isGroupBySinkEnabled,
                                            restrictedEditRole,
                                            ...extraDataProps
                                          }
                                        }}
                                      >
                                        {children}
                                      </VulnerabilitiesGridContext.Provider>
                                    )
                                  }

                                  if (
                                    queries.severitySnapshotsQuery &&
                                    queries.discoverySnapshotsQuery &&
                                    !!chartVisualization &&
                                    !isChartContainerCollapsed // calculate/load snapshots only when the chart visualization is displayed
                                  ) {
                                    return (
                                      <ChartLoader
                                        interval={getChartSnapshotInterval(
                                          dateRangeOption,
                                          dateRange
                                        )}
                                        vulnerabilitiesSeverityChartQuery={
                                          queries.severitySnapshotsQuery
                                        }
                                        vulnerabilitiesDiscoveryChartQuery={
                                          queries.discoverySnapshotsQuery
                                        }
                                        organizationID={organizationID}
                                        quickFilterID={quickFilterID}
                                        filterText={filterText}
                                        aggregationFilters={
                                          aggregationSelections
                                        }
                                        additionalFilters={additionalFilters}
                                        refetchQueries={refetchQueries}
                                      >
                                        {({
                                          severityChartSnapshots,
                                          discoveryChartSnapshots,
                                          refetchQueries,
                                          loadingSnapshots
                                        }) => {
                                          return VulnerabilitiesGridContextProvider(
                                            {
                                              severityChartSnapshots,
                                              discoveryChartSnapshots,
                                              refetchQueries,
                                              loadingSnapshots
                                            }
                                          )
                                        }}
                                      </ChartLoader>
                                    )
                                  }
                                  return VulnerabilitiesGridContextProvider()
                                }}
                              </GridLoader>
                            )
                          }}
                        />
                      )
                    }}
                  </ChartVisualizationManager>
                )
              }}
            </MetadataFilterManager>
          )
        }}
      </GridStateManager>
    )
  }
}

type Tutorial =
  | 'APPLICATIONS'
  | 'APP_OVERVIEW'
  | 'APP_LIBRARIES'
  | 'APP_VULNERABILITIES'
  | 'ATTACKS'

interface ChartContainerProps<TChartVisualization = {}> {
  onCollapse: () => void
  chartVisualization: TChartVisualization
  onChartVisualizationToggle: (chartVisualization: TChartVisualization) => void

  severityChartSnapshots: VulnerabilitySeveritySnapshotBreakdown[]
  discoveryChartSnapshots: VulnerabilityDiscoverySnapshotBreakdown[]
  sessionMetadataSelections?: string[]
  dateRangeOption?: DateRangeOption
  dateRange?: DateRange
  loadingSnapshots?: boolean
}

export interface BulkActionProps<TData, TAggregationCategory extends string> {
  rowSelectionProps: RowSelection<TData, TAggregationCategory>
  organizationID: string
  organizationRole: Role
  features: AgentFeature[]
  isFreemiumOrganization: boolean
  refetchQueries: PureQueryOptions[]
  refresh: () => Promise<any>
  trackJob: (job: {
    id: string
    total: number
    progress: number
  }) => Promise<void>
  restrictedEditRole?: boolean
}
export interface GridProps<
  TAggregationCategory extends string,
  TChartVisualization extends {} = {}
> {
  renderBulkActions: (
    props: BulkActionProps<
      GQLVulnerability | GQLVulnerabilityOrSinkGroup,
      TAggregationCategory
    >
  ) => ReactElement<any>
  columns: (
    args: ColumnArgs<TAggregationCategory>
  ) => Array<Column<Vulnerability, TAggregationCategory>>
  isDrilldown: boolean
  showLicensedOnlyCheckbox?: boolean
  tutorialKey?: Tutorial
  applicationId?: string
  rootScope?: {
    $on: (eventName: string, callback: any) => void
  }
  renderChartToggle?: (
    renderChartToggleProps: ChartToggleProps
  ) => React.ReactNode
  renderChartContainer?: (
    renderChartContainerProps: ChartContainerProps<TChartVisualization>
  ) => React.ReactNode
  chartContainerHeight?: number
  headerExtraChildren?: (props: any) => void
}
interface AdditionalCollectionProps extends LicensedCount {
  rootScope?: {
    $on: (eventName: string, callback: any) => void
  }
}
export function createVulnerabilitiesGrid<
  TOrderField extends string,
  TAggregationCategory extends string,
  TChartVisualization extends {} = {}
>(
  VulnerabilitiesGridContext: React.Context<
    VulnerabilitiesGridContextValue<
      TOrderField,
      TAggregationCategory,
      AdditionalCollectionProps,
      TChartVisualization
    >
  >
) {
  class VulnerabilitiesGrid extends React.Component<
    GridProps<TAggregationCategory, TChartVisualization> & {
      gridContext: VulnerabilitiesGridContextValue<
        TOrderField,
        TAggregationCategory,
        AdditionalCollectionProps,
        TChartVisualization
      >
    }
  > {
    private unregisterUpdateHandler

    public componentDidMount() {
      const { rootScope, data } = this.props.gridContext

      if (data) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        data.refresh()

        if (rootScope) {
          this.unregisterUpdateHandler = rootScope.$on(
            'application:reload',
            data.refresh
          )
        }
      }
    }

    public componentWillUnmount() {
      if (this.unregisterUpdateHandler) {
        this.unregisterUpdateHandler()
      }
    }

    public render() {
      const {
        columns,
        headerExtraChildren,
        renderBulkActions,
        renderChartToggle,
        renderChartContainer,
        chartContainerHeight,
        isDrilldown,
        showLicensedOnlyCheckbox = false,
        tutorialKey,
        applicationId,
        gridContext: {
          order,
          orderFieldConfig,
          defaultOrderField,
          onSetSortField,
          onSetSortDirection,
          filterText,
          onFilterTextChange,
          quickFilterID,
          onQuickFilterIDChange,
          sessionMetadataFieldID,
          onSessionMetadataFieldIDChange,
          metadataSelections,
          onClearMetadataSelections,
          onMetadataOptionsToggle,
          aggregationSelections,
          aggregationConfigs,
          onGroupToggle,
          onMultiGroupToggle,
          onOptionToggle,
          onAggregationCategoriesClear,
          isChartContainerCollapsed,
          chartVisualization,
          onChartContainerToggle,
          onChartVisualizationToggle,
          updateCellHeight,
          registerListRef,
          maxCellHeightsByRow,
          dateRangeOption,
          dateRange,
          onSetCustomDateRange,
          onSetDateRangeOption,
          onClearDateRange,
          dateField,
          onSetDateField,
          toggleQueryParameters,
          protectAssessEnv,
          isProtectColumnEnabled,
          isProtectEnabled,
          data
        }
      } = this.props

      if (!data) {
        return false
      }

      const {
        features,
        totalRowCount,
        loadMore,
        organizationID,
        organizationRole,
        organizationType,
        assessLicenseBreakdown,
        quickFilters,
        aggregations,
        sessionMetadataFields,
        sessionMetadataAggregations,
        severityChartSnapshots,
        discoveryChartSnapshots,
        loadingSnapshots,
        loadedRowCount,
        rowDataValue,
        rowGetter,
        isRowLoaded,
        refetchQueries,
        refresh,
        additionalCollectionProps,
        additionalFilters,
        licensedOnly,
        isSinkDrilldown,
        isGroupBySinkEnabled,
        restrictedEditRole
      } = data

      const RowQuery = getVulnerabilityOrSinkGroup(organizationID)

      const isFreemiumOrganization = organizationType === 'Freemium'
      const hasLicenses = assessLicenseBreakdown.unused > 0

      const licensedVulnCount =
        additionalCollectionProps.licensedVulnerabilityCount
      const showGroupBySink = !!licensedVulnCount && licensedVulnCount > 0

      return (
        <React.Fragment>
          <LegacyGridContainer>
            <JobTracker>
              {({ trackedJob, trackJob }) => {
                return (
                  <Fragment>
                    <Grid<
                      Vulnerability & VulnerabilityOrSinkGroup,
                      TOrderField,
                      TAggregationCategory
                    >
                      isDrilldown={isDrilldown}
                      getAllCount={({ toggled }) => {
                        if (licensedVulnCount) {
                          return licensedVulnCount - toggled
                        }
                        return 0
                      }}
                      updateCellHeight={updateCellHeight}
                      registerListRef={registerListRef}
                      maxCellHeightsByRow={maxCellHeightsByRow}
                      filterValue={{ dateRange, dateField }}
                      gridDataValue={{ dateField }}
                      RowQuery={RowQuery}
                      loadedRowCount={loadedRowCount}
                      rowDataValue={rowDataValue}
                      isRowLoaded={isRowLoaded}
                      rowGetter={rowGetter}
                      loadMore={loadMore}
                      totalRowCount={totalRowCount}
                      rowHeight={(vulnerability) => {
                        return (
                          (vulnerability &&
                            maxCellHeightsByRow[vulnerability.id]) ||
                          minRowHeight
                        )
                      }}
                      renderBulkActions={(rowSelectionProps) => {
                        return renderBulkActions({
                          rowSelectionProps: {
                            ...(rowSelectionProps as any),
                            additionalFilters
                          },
                          organizationID,
                          organizationRole,
                          features,
                          isFreemiumOrganization,
                          refetchQueries,
                          refresh,
                          trackJob,
                          restrictedEditRole
                        })
                      }}
                      titleMessage={{
                        id: 'title_vulnerabilities',
                        defaultMessage: 'Vulnerabilities'
                      }}
                      searchPlaceholderMessage={{
                        id: 'placeholder_findVulnerability',
                        defaultMessage: 'Find Vulnerability'
                      }}
                      order={order}
                      onSetSortField={(value) => {
                        onSetSortField(value)
                        sendDefaultEvent('vulnerabilities.grid.sort', value)
                      }}
                      onSetSortDirection={onSetSortDirection}
                      orderFieldConfig={orderFieldConfig}
                      defaultOrderField={defaultOrderField}
                      onFilterTextChange={(value) => {
                        onFilterTextChange(value)
                        sendDefaultEvent('vulnerabilities.grid.search', value)
                      }}
                      filterText={filterText}
                      onQuickFilterIDChange={onQuickFilterIDChange}
                      quickFilterID={quickFilterID}
                      quickFilters={quickFilters}
                      quickFilterToGroup={quickFilterToGroup}
                      aggregations={aggregations}
                      aggregationConfigs={aggregationConfigs}
                      aggregationSelections={aggregationSelections}
                      sessionMetadataFieldID={
                        // session metadata field being truthy is part of determining
                        // visibility of the field dropdown buried inside the grid/grid-header component
                        // hide it when we have a agent session filter set
                        aggregationSelections.hasOwnProperty('agentSessionId')
                          ? ''
                          : sessionMetadataFieldID
                      }
                      sessionMetadataFields={sessionMetadataFields}
                      sessionMetadataFieldToGroup={sessionMetadataFieldToGroup}
                      onSessionMetadataFieldIDChange={
                        onSessionMetadataFieldIDChange
                      }
                      onGroupToggle={onGroupToggle}
                      onMultiGroupToggle={onMultiGroupToggle}
                      onOptionToggle={onOptionToggle}
                      onAggregationCategoriesClear={
                        onAggregationCategoriesClear
                      }
                      batchSize={batchSize}
                      isRowSelectable={(rowData) =>
                        rowData.vulnerability?.visible || rowData.visible
                      }
                      columns={columns({
                        aggregations,
                        organizationID,
                        organizationRole,
                        dateRange,
                        dateRangeOption,
                        onSetCustomDateRange,
                        onSetDateRangeOption,
                        onClearDateRange,
                        dateField,
                        onSetDateField,
                        hasLicenses,
                        features,
                        refetchQueries,
                        refresh,
                        trackJob,
                        sessionMetadataFieldID,
                        sessionMetadataAggregations,
                        metadataSelections,
                        onClearMetadataSelections,
                        onMetadataOptionsToggle,
                        restrictedEditRole,
                        protectAssessEnv,
                        isProtectColumnEnabled,
                        isProtectEnabled
                      })}
                      headerPreOrderSelectorChildren={
                        <HeaderCheckboxes
                          showLicensedOnlyCheckbox={showLicensedOnlyCheckbox}
                          licensedOnly={!!licensedOnly}
                          showGroupBySink={showGroupBySink}
                          isGroupBySinkEnabled={!!isGroupBySinkEnabled}
                          toggleQueryParameters={toggleQueryParameters}
                          isSinkDrilldown={!!isSinkDrilldown}
                        />
                      }
                      headerExtraChildren={
                        <>
                          {typeof headerExtraChildren === 'function' &&
                            headerExtraChildren(this.props)}
                          <BackToGroups
                            aggregationsToClear={Object.keys(
                              aggregationConfigs
                            )}
                          />
                          {isChartContainerCollapsed &&
                            !!renderChartToggle &&
                            renderChartToggle({
                              onClick: onChartContainerToggle
                            })}
                        </>
                      }
                      headerExpandRow={
                        !isChartContainerCollapsed &&
                        !!renderChartContainer &&
                        !!chartContainerHeight &&
                        !!chartVisualization &&
                        !!severityChartSnapshots &&
                        !!discoveryChartSnapshots
                          ? {
                              children: renderChartContainer({
                                chartVisualization,
                                onCollapse: onChartContainerToggle,
                                onChartVisualizationToggle,
                                severityChartSnapshots,
                                discoveryChartSnapshots,
                                sessionMetadataSelections: !!metadataSelections
                                  ? getSessionMetadataSelectionsValues(
                                      metadataSelections
                                    )
                                  : [],
                                dateRangeOption,
                                dateRange,
                                loadingSnapshots
                              }),
                              height: chartContainerHeight
                            }
                          : undefined
                      }
                    />
                    {trackedJob && (
                      <DeleteOverlay
                        total={trackedJob.total}
                        progress={trackedJob.progress}
                      />
                    )}
                  </Fragment>
                )
              }}
            </JobTracker>
          </LegacyGridContainer>
          {isFreemiumOrganization && tutorialKey && (
            <SlidingTutorial
              isOpen={isFreemiumOrganization}
              isVisible={isFreemiumOrganization}
              tutorialKey={tutorialKey}
              orgUuid={organizationID}
              yOffset={30} // This value depends on onboarding header size and visibility
              appId={applicationId}
              zIndex={1002}
            />
          )}
        </React.Fragment>
      )
    }
  }

  return function WrappedVulnerabilitiesGrid(
    props: GridProps<TAggregationCategory>
  ) {
    return (
      <VulnerabilitiesGridContext.Consumer>
        {(context) => {
          return <VulnerabilitiesGrid {...props} gridContext={context} />
        }}
      </VulnerabilitiesGridContext.Consumer>
    )
  }
}

export const filterAggregations = (
  aggregations: Record<string, string[]>
): Record<string, string[]> => {
  Object.keys(aggregations).forEach((key) => {
    if (key.startsWith('application-metadata-')) {
      delete aggregations[key]
    }
  })

  return aggregations
}
